import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Filter, X, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, CATEGORIES, CITIES } from "./constants";
import { ProductForm } from "./ProductForm";

export function ProductsTab({ password }) {
  const [products, setProducts] = useState([]);
  const [filterCity, setFilterCity] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [editProduct, setEditProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { password };
      if (filterCity) params.city = filterCity;
      const res = await axios.get(`${API}/admin/products`, { params });
      setProducts(res.data);
    } catch {
      toast.error("Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, [password, filterCity]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleDelete = async (product) => {
    if (!window.confirm(`Удалить "${product.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${product.id}?password=${encodeURIComponent(password)}`);
      toast.success("Удалено");
      fetchProducts();
    } catch {
      toast.error("Ошибка удаления");
    }
  };

  const handleFormSave = () => {
    setShowForm(false);
    setEditProduct(null);
    fetchProducts();
  };

  const filteredProducts = products
    .filter((p) => !filterCategory || p.category === filterCategory)
    .filter((p) => !priceMin || p.price >= parseFloat(priceMin))
    .filter((p) => !priceMax || p.price <= parseFloat(priceMax))
    .sort((a, b) => {
      if (sortOrder === "asc") return a.price - b.price;
      if (sortOrder === "desc") return b.price - a.price;
      return 0;
    });

  const resetFilters = () => {
    setFilterCity("");
    setFilterCategory("");
    setPriceMin("");
    setPriceMax("");
    setSortOrder("");
  };

  const hasActiveFilters = filterCity || filterCategory || priceMin || priceMax || sortOrder;

  return (
    <>
      {/* Toolbar */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Каталог букетов</h1>
            <p className="text-gray-500 text-sm">{filteredProducts.length} из {products.length} товаров</p>
          </div>
          <Button
            onClick={() => { setEditProduct(null); setShowForm(true); }}
            className="bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-bold flex items-center gap-2 self-start sm:self-auto"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4" />
            Добавить
          </Button>
        </div>

        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <Filter className="w-4 h-4 text-gray-400 hidden sm:block" />

          <select
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium focus:border-pink-400 outline-none bg-white min-w-[120px]"
            data-testid="admin-city-filter"
          >
            <option value="">Все города</option>
            {CITIES.map((c) => (
              <option key={c.slug} value={c.slug}>{c.name}</option>
            ))}
          </select>

          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium focus:border-pink-400 outline-none bg-white min-w-[120px]"
            data-testid="admin-category-filter"
          >
            <option value="">Все категории</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <div className="flex items-center gap-1.5">
            <input
              type="number"
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              placeholder="От"
              className="w-20 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-pink-400 outline-none"
              data-testid="admin-price-min"
            />
            <span className="text-gray-400 text-sm">—</span>
            <input
              type="number"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              placeholder="До"
              className="w-20 px-3 py-2 rounded-xl border border-gray-200 text-sm focus:border-pink-400 outline-none"
              data-testid="admin-price-max"
            />
          </div>

          <button
            onClick={() => setSortOrder(prev => prev === "asc" ? "desc" : prev === "desc" ? "" : "asc")}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
              sortOrder 
                ? "border-pink-300 bg-pink-50 text-pink-600" 
                : "border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
            data-testid="admin-sort-price"
          >
            {sortOrder === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : sortOrder === "desc" ? <ArrowDown className="w-3.5 h-3.5" /> : <ArrowUpDown className="w-3.5 h-3.5" />}
            Цена
          </button>

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              data-testid="admin-reset-filters"
            >
              <X className="w-3.5 h-3.5" />
              Сбросить
            </button>
          )}
        </div>
      </div>

      {/* Product Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-10 h-10 border-2 border-pink-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-500">Загрузка...</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow group"
              data-testid={`admin-product-${product.id}`}
            >
              <div className="aspect-square relative overflow-hidden bg-gray-100">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Actions overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  <button
                    onClick={() => { setEditProduct(product); setShowForm(true); }}
                    className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                    data-testid={`edit-btn-${product.id}`}
                  >
                    <Pencil className="w-4 h-4 text-gray-700" />
                  </button>
                  <button
                    onClick={() => handleDelete(product)}
                    className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                    data-testid={`delete-btn-${product.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
                {/* City badge */}
                <div className="absolute top-2 left-2">
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-white/90 text-gray-700 shadow">
                    {CITIES.find(c => c.slug === product.city)?.name || product.city}
                  </span>
                </div>
              </div>
              <div className="p-3">
                <p className="font-bold text-sm text-gray-900 truncate">{product.name}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-bold text-pink-500">{CITIES.find(c => c.slug === product.city)?.currency || "$"}{product.price}</span>
                  <span className="text-xs text-gray-400 capitalize">{product.category}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredProducts.length === 0 && !loading && (
        <div className="text-center py-16">
          <p className="text-gray-500 mb-4">Нет букетов</p>
          <Button
            onClick={() => { setEditProduct(null); setShowForm(true); }}
            className="bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl"
          >
            Добавить первый букет
          </Button>
        </div>
      )}

      {/* Product Form Modal */}
      {showForm && (
        <ProductForm
          product={editProduct}
          password={password}
          onSave={handleFormSave}
          onCancel={() => { setShowForm(false); setEditProduct(null); }}
        />
      )}
    </>
  );
}
